import React from 'react'
import {Badge, Button, Card, Col, ListGroup, ListGroupItem, Row} from 'react-bootstrap'
import { useLocation, useNavigate } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { IRegistration } from '../../../store/registrations/interfaces'
import { IEvent } from '../../../store/events/interfaces'
import { isTabletOrMobileQuery, isTabletOrSmallDesktopQuery } from '../../../utils'
import { useMediaQuery } from 'react-responsive'
import { stages } from '../../../routes'
import { AddValidatorFunc } from '../../../hooks/validators'

import FamilyData from './FamilyData'
import PriceCategory from './PriceCategory'
import GroupCode from './GroupCode'
import CustomQuestion from './CustomQuestion'
import AdditionalData from './AdditionalData'
import Priority from './Priority'
import { useAppSelector } from '../../../hooks'
import { useSelector } from 'react-redux'
import { RootState } from '../../../store'
import { DistributionType } from '../../../store/program/interfaces'

export const RegistrationListItem = (props: {
  registration: IRegistration,
  editable: boolean,
  addValidator?: AddValidatorFunc,
  isSingle?: boolean
}) => {
  const { registration, editable, addValidator ,isSingle} = props
  const { is_group_registration_enabled, is_group_strict, requires_family_data } = useAppSelector(
    (state) => state.program.data,
  )
  const event = useAppSelector((state) =>
    state.events.list.find(
      (event: IEvent) => event.event_id === registration.event.event_id,
    ),
  )

  const { data: institution } = useAppSelector(
    (state) => state.institution,
  )

  const location = useLocation()
  const parentStage = stages.find(stage =>
    location.pathname.includes(stage.path),
  )

  const { distribution_type } = useSelector(
    (state: RootState) => state.program.data,
  )

  const navigate = useNavigate()
  const isTabletOrMobile = useMediaQuery(isTabletOrMobileQuery)
  const isTabletOrSmallDesktop = useMediaQuery(isTabletOrSmallDesktopQuery)

  const [isExpanded, setIsExpanded] = React.useState(!registration.loadedFromBackend)
  const enableExpandedMode = isTabletOrMobile || isTabletOrSmallDesktop

  const [errors, setErrors] = React.useState<boolean[]>(
    new Array(4 + registration.event.registration_custom_fields.length).fill(
      false,
    ),
  )

  // check for custom configurations
  const displayPriceCategories =
    !('registration-stage' in institution.custom_configuration) ||
    !('behavior' in institution.custom_configuration['registration-stage']) ||
    !(
      institution.custom_configuration['registration-stage']['behavior'] ===
      'hide-categories'
    )

  const setError = (idx: number) => (value: boolean) =>
    setErrors(errors.map((error: boolean, key: number) => key === idx && value))

  if (!event) {
    console.log(`Couldn't find event with id ${registration.event.event_id}`)
    return <></>
  } else {
    const {
      name,
      relative_id,
      price_categories,
      price,
      label_special_notes,
      duration_summary,
    } = event
    return (
      <Col md={isSingle ? 12 : 6}>
        <Card
          className={`m-3 ${errors.some(val => val) ? 'border-danger' : 'border-dark'
            }`}
          id={`reg-card-${event.event_id}`}
        >
          <Card.Header>
            <Row>
              <Col md={enableExpandedMode ? 8 : 10}>
                <h4>{name} (#{relative_id})</h4>
                {!!duration_summary && (
                  <>
                    {duration_summary}
                  </>
                )}
              </Col>
              <Col
                md={enableExpandedMode ? 4 : 2}
                className='h3 d-flex justify-content-end align-items-right'
              >
                {enableExpandedMode ? (
                  <Button
                    variant='outline-dark'
                    aria-label={
                      isExpanded
                        ? 'Veranstaltung minimieren'
                        : 'Veranstaltung erweitern'
                    }
                    className={`mobile-btn mr-1 ${isTabletOrMobile ? 'btn-lg' : undefined
                      }`}
                    onClick={() => {
                      setIsExpanded(!isExpanded)
                    }}
                    aria-expanded={isExpanded}
                  >
                    <FontAwesomeIcon icon={isExpanded ? 'angle-up' : 'angle-down'} />
                  </Button>
                ) : null}
                {parentStage && (
                  <Button
                    variant='outline-dark'
                    aria-label='Veranstaltungsdetails'
                    className={`mobile-btn ${isTabletOrMobile ? 'btn-lg' : undefined
                      }`}
                    onClick={() => {
                      navigate(
                        `${parentStage.path}/${registration.event.event_id}`,
                      )
                    }}
                  >
                    <FontAwesomeIcon icon='info-circle' role='presentation' />
                  </Button>
                )}
              </Col>
            </Row>
          </Card.Header>
          <ListGroup className='list-group-flush' hidden={!isExpanded && enableExpandedMode}>
            {price_categories.length > 0 && displayPriceCategories ? (
              <PriceCategory
                registration={registration}
                categories={price_categories}
                event_price={price}
                editable={editable}
                setError={setError(0)}
                addValidator={addValidator}
              />
            ) : (
              <ListGroupItem className='m-0'>
                <div className='d-flex justify-content-between align-items-center'>
                    <strong>Veranstaltungspreis:</strong>
                    <div>
                        {event.price || registration.price
                            ? registration.price
                                ? registration.price.toFixed(2) + ' €'
                                : event.price.toFixed(2) + ' €'
                            : 'Kostenlos'}
                    </div>
                </div>
                {registration.event.has_external_payment && (
                    <div className="mt-2 d-flex align-items-center">
                        <Badge variant='warning' tabIndex={0} className='mr-2'>
                            Achtung
                        </Badge>
                        <small className="text-muted">Bezahlung wird direkt über den Veranstalter geregelt!</small>
                    </div>
                )}
              </ListGroupItem>
            )}
            {requires_family_data && (
              <FamilyData
                registration={registration}
                editable={editable}
                setError={setError(1)}
                addValidator={addValidator}
              />
            )}
            {is_group_registration_enabled && (
              <GroupCode
                registration={registration}
                strict_mode={is_group_strict}
                editable={editable}
                setError={setError(2)}
                addValidator={addValidator}
              />
            )}

            {registration.event.registration_custom_fields.map(
              (question, key) => (
                <CustomQuestion
                  key={`custom-question-${key}`}
                  registration={registration}
                  question={question}
                  editable={editable}
                  setError={setError(4 + key)}
                  addValidator={addValidator}
                />
              ),
            )}
            <AdditionalData
              registration={registration}
              editable={editable}
              labelSpecialNotes={label_special_notes}
              setError={setError(3)}
              addValidator={addValidator}
            />
          </ListGroup>
          {distribution_type !== DistributionType.INSTANT && (
            <Card.Footer className='border-top-0'>
              <Priority registration={registration} editable={editable} />
            </Card.Footer>
          )}
        </Card>
      </Col>
    )
  }
}
